import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import * as actions from './actions'
import {
    Loading,
    Modal,
    PageTitle,
    ReportStatus,
    Tooltip
} from '../root/components/common'
import {
    getFormattedDate,
    getReportTypeDescriptionText,
    getReportTypeText
} from 'web/src/utils'
import { arrayBufferToBase64 } from 'web/src/utils/bufferToBase64'
import Lightbox from 'react-image-lightbox'
import 'react-image-lightbox/style.css'
import { useBooleanState } from 'web/src/utils/hooks/useBooleanState'
import Button from '../root/components/common/button'
import ReportEmailSender from '../root/components/common/reports-table/reports-table-email-sender'
import { UserPermissions } from 'web/src/services/permissions'
import { enqueueSnackbar } from '../root/actions'
import { CommentCard } from './partials'
import { useStyles } from './styles'
import Loader from 'web/src/app/root/components/common/loader'
import { mdiBug, mdiTools } from '@mdi/js'
import Icon from '@mdi/react'
import { ReportCatchModal } from '../devices/components/report-catch-modal'
import { useHistory } from 'react-router-dom'
import { ReportReplaceTypeModal } from './report-replace-type-modal'
import { REPORT_TYPES } from '../root/hooks/useReportTypes'
import { CatchReplacedIcon } from '../root/components/common/catch-replaced-icon'
import { WarningReplacedIcon } from '../root/components/common/warning-replaced-icon'

function Report(props) {
    const classes = useStyles()
    const dispatch = useDispatch()
    const history = useHistory()
    const id = props.match.params.id
    const lightbox = useBooleanState(false)
    const { isLoading, theme, locale } = useSelector(state => state.main)

    const {
        report,
        comments,
        commentsPictures,
        imagesLoader,
        reportSharingHistory
    } = useSelector(state => state.reports)
    const loggedInUser = useSelector(state => state.main.user)
    const commentInputRef = useRef(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [commentText, setCommentText] = useState('')
    const [isEdit, setIsEdit] = useState(false)
    const [commentId, setCommentId] = useState()
    const [addComment, setAddComment] = useState(false)
    const [isDeleteCommentModalOpen, setIsDeleteCommentModalOpen] = useState(
        false
    )
    const [commentIdToDelete, setCommentIdToDelete] = useState()
    const [isReportCatchModalOpen, setIsReportCatchModalOpen] = useState(false)
    const [isReplaceTypeModalOpen, setIsReplaceTypeModalOpen] = useState(false)
    const [replaceType, setReplaceType] = useState(null)

    const permissions = {
        updateDevice:
            (UserPermissions.report(report?.customer_id).update &&
                report?.is_assigned) ||
            (report?.is_original && UserPermissions.isAllOriginalGranted()),
        canAddComment:
            (UserPermissions.report(report?.customer_id).read &&
                report?.is_assigned) ||
            (report?.is_original && UserPermissions.isAdmin())
    }

    const onReportCatchClick = () => {
        setIsReportCatchModalOpen(true)
    }

    const closeReportCatchModal = () => setIsReportCatchModalOpen(false)

    const openReplaceTypeModal = rType => {
        setReplaceType(rType)
        setIsReplaceTypeModalOpen(true)
    }
    const closeReplaceTypeModal = () => {
        setReplaceType(null)
        setIsReplaceTypeModalOpen(false)
    }

    const [imagesForUpload, setImagesForUpload] = useState([])
    const [deletedPictureKeys, setDeletedPictureKeys] = useState([])
    const wrapperRef = useRef(null)

    const hasCatchDescription = report => {
        return (
            report?.data?.catch_description &&
            typeof report.data.catch_description === 'string' &&
            report.data.catch_description.replace(/\s+/g, '') !== ''
        )
    }

    const postComment = (commentContent, report) => {
        if (isEdit) {
            dispatch(
                actions.editComment({
                    commentId: commentId,
                    entity_id: id,
                    entity_type: 'reports',
                    comment: {
                        text: commentContent,
                        customer_id: report.customer_id,
                        images: imagesForUpload.filter(image => !image.key),
                        deletedPictureKeys: deletedPictureKeys.filter(
                            item => item.key
                        )
                    }
                })
            )

            setAddComment(false)
            setImagesForUpload([])
            setCommentId(null)
            setDeletedPictureKeys([])
        } else {
            if (commentText !== '') {
                dispatch(
                    actions.postComment({
                        comment: {
                            entity_id: report.id,
                            entity_type: 'reports',
                            customer_id: report.customer_id,
                            text: commentContent,
                            images: imagesForUpload
                        }
                    })
                )
                setAddComment(false)
                setCommentId(null)
                setImagesForUpload([])
            } else {
                dispatch(
                    enqueueSnackbar({
                        message: locale.reports.commentEmptyEx,
                        options: {
                            variant: 'warning'
                        }
                    })
                )
            }
        }
    }

    const deleteComment = (commentId, report) => {
        dispatch(
            actions.deleteComment({
                commentId: commentId,
                customer_id: report.customer_id,
                entity_id: report.id,
                entity_type: 'reports'
            })
        )
        setIsDeleteCommentModalOpen(false)
    }

    const getMap = () => {
        if (!commentInputRef.current) {
            commentInputRef.current = new Map()
        }
        return commentInputRef.current
    }

    const editComment = async (commentContent, commentId) => {
        const map = getMap(commentId)
        const node = map.get(commentId)

        setImagesForUpload(commentsPictures[commentId])
        setAddComment(true)
        setIsEdit(true)
        setCommentText(commentContent)
        setCommentId(commentId)
        node?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
        })
    }

    const hasTomPicture = report?.tom_cam_picture_id && report?.tom_cam_picture

    const tomPictureFileLink = hasTomPicture
        ? 'data:image/jpeg;base64,' +
          arrayBufferToBase64(report.tom_cam_picture.file?.Body?.data)
        : null

    const reloadReport = useCallback(() => {
        dispatch(actions.reportRequest(id))
        dispatch(actions.reportSharingHistoryRequest(id))
        dispatch(
            actions.requestComment({
                entity_id: id,
                entity_type: 'reports'
            })
        )
    }, [id])

    useEffect(() => {
        dispatch(actions.reportRequest(id))
        dispatch(actions.reportSharingHistoryRequest(id))
        dispatch(
            actions.requestComment({
                entity_id: id,
                entity_type: 'reports'
            })
        )
        return () => {
            dispatch(actions.reportRequestSuccess(null))
            dispatch(actions.reportSharingHistoryRequestSuccess(null))
            dispatch(actions.requestCommentSuccess(null))
        }
    }, [id])

    return (
        <div className={classes.wrapper} ref={wrapperRef}>
            {isModalOpen ? (
                <ReportEmailSender
                    reportData={report}
                    setIsModalOpen={setIsModalOpen}
                />
            ) : null}
            {isLoading && <Loading />}
            {!isLoading && !report && (
                <div>{locale.common.nothingWasFound}</div>
            )}
            {!isLoading && report && (
                <>
                    {isReplaceTypeModalOpen && replaceType !== null ? (
                        <ReportReplaceTypeModal
                            reportId={report.id}
                            onClose={closeReplaceTypeModal}
                            open={isReplaceTypeModalOpen}
                            replaceType={replaceType}
                            reloadReport={reloadReport}
                        />
                    ) : null}
                    <div className={classes.header}>
                        <PageTitle title={report.name} />
                        <div className={classes.headerButtonsWrapper}>
                            {report.report_type === '2'
                                ? permissions.updateDevice && (
                                      <Button onClick={onReportCatchClick}>
                                          <div
                                              className={classes.iconUnderlay}
                                              style={{
                                                  backgroundColor: theme.error
                                              }}
                                          >
                                              <Icon
                                                  path={mdiBug}
                                                  size={'20px'}
                                                  color="#FFF"
                                              />
                                          </div>{' '}
                                          {locale.devices.reportCatch}
                                      </Button>
                                  )
                                : null}
                            {report.report_type === '2' ? (
                                <Button
                                    // className={classes.sendReportButton}
                                    onClick={() => setIsModalOpen(true)}
                                >
                                    {locale.reports.sendReport}
                                </Button>
                            ) : null}
                        </div>
                        <Modal
                            open={isReportCatchModalOpen}
                            setOpen={setIsReportCatchModalOpen}
                        >
                            <ReportCatchModal
                                contextPage="report"
                                deviceId={report.device_id}
                                createdAt={report.created_at}
                                onClose={closeReportCatchModal}
                            />
                        </Modal>
                    </div>

                    <div className={classes.detailsWrapper}>
                        <div className={classes.detailsInfoWrapper}>
                            <div className={classes.detailsRow}>
                                <div className={classes.detailsNameColumn}>
                                    {locale.reports.location}:
                                </div>
                                <div
                                    className={`${classes.detailsValueColumn} ${classes.clickableText}`}
                                    onClick={() =>
                                        history.push(
                                            `/locations/${report.location_id}`
                                        )
                                    }
                                >
                                    {report.location_name}
                                </div>
                            </div>
                            <div className={classes.detailsRow}>
                                <div className={classes.detailsNameColumn}>
                                    {locale.reports.deviceName}:
                                </div>
                                <div className={classes.detailsValueColumn}>
                                    {locale.devices.deviceTypes[
                                        report.device_type
                                    ] || report.device_type}
                                </div>
                            </div>
                            <div className={classes.detailsRow}>
                                <div className={classes.detailsNameColumn}>
                                    {locale.reports.deviceSerial}:
                                </div>
                                <div
                                    className={`${classes.detailsValueColumn} ${classes.clickableText}`}
                                    onClick={() =>
                                        history.push(
                                            `/devices/${report.device_id}`
                                        )
                                    }
                                >
                                    {report.device_serial_number}
                                </div>
                            </div>
                            <div className={classes.detailsRow}>
                                <div className={classes.detailsNameColumn}>
                                    {locale.devices.deviceOperationMode}:
                                </div>
                                <div className={classes.detailsValueColumn}>
                                    {locale.devices.operationMode[
                                        report.device_operation_mode
                                    ] || report.device_operation_mode}
                                </div>
                            </div>
                            <div className={classes.detailsRow}>
                                <div className={classes.detailsNameColumn}>
                                    {locale.devices.deviceType}:
                                </div>
                                <div className={classes.detailsValueColumn}>
                                    {locale.devices.deviceTypes[
                                        report.device_type
                                    ] || report.device_type}
                                </div>
                            </div>
                            <div className={classes.detailsRow}>
                                <div className={classes.detailsNameColumn}>
                                    {locale.reports.reportTime}:
                                </div>
                                <div className={classes.detailsValueColumn}>
                                    {report.tom_cam_picture &&
                                    report.tom_cam_picture.created_at
                                        ? getFormattedDate(
                                              report.tom_cam_picture.created_at
                                          )
                                        : getFormattedDate(report.created_at)}
                                </div>
                            </div>
                            {report.tom_cam_picture &&
                                report.tom_cam_picture.created_at && (
                                    <div className={classes.detailsRow}>
                                        <div
                                            className={
                                                classes.detailsNameColumn
                                            }
                                        >
                                            {locale.reports.evaluationTime}:
                                        </div>
                                        <div
                                            className={
                                                classes.detailsValueColumn
                                            }
                                        >
                                            {getFormattedDate(
                                                report.created_at
                                            )}
                                        </div>
                                    </div>
                                )}
                            <div className={classes.detailsRow}>
                                <div className={classes.detailsNameColumn}>
                                    {locale.reports.event}:
                                </div>
                                <div
                                    className={
                                        classes.detailsValueColumnReportDescr
                                    }
                                >
                                    <div className={classes.reportTypeWithIcon}>
                                        {report.in_maintenance_mode ? (
                                            <Tooltip
                                                hover
                                                placement="top"
                                                text={
                                                    locale.common
                                                        .maintenanceMode
                                                }
                                            >
                                                <Icon
                                                    path={mdiTools}
                                                    size={0.8}
                                                    color={theme.primary}
                                                />
                                            </Tooltip>
                                        ) : null}
                                        <div>
                                            {report.replaced_type ===
                                                REPORT_TYPES.catch ||
                                            report.replaced_type ===
                                                REPORT_TYPES.false_triggering ? (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 5
                                                    }}
                                                >
                                                    {report.replaced_type ===
                                                    REPORT_TYPES.catch ? (
                                                        <CatchReplacedIcon
                                                            size={24}
                                                        />
                                                    ) : null}
                                                    {report.replaced_type ===
                                                    REPORT_TYPES.false_triggering ? (
                                                        <WarningReplacedIcon
                                                            size={24}
                                                        />
                                                    ) : null}
                                                    <div
                                                        style={{
                                                            textDecoration:
                                                                'line-through'
                                                        }}
                                                    >
                                                        {getReportTypeText(
                                                            {
                                                                ...report,
                                                                report_type:
                                                                    report.replaced_type
                                                            },
                                                            locale
                                                        )}
                                                    </div>
                                                </div>
                                            ) : null}
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 5
                                                }}
                                            >
                                                {report.replaced_type ===
                                                    REPORT_TYPES.catch ||
                                                report.replaced_type ===
                                                    REPORT_TYPES.false_triggering
                                                    ? '→'
                                                    : null}
                                                <ReportStatus
                                                    report={report}
                                                    theme={theme}
                                                />
                                                <div>
                                                    {getReportTypeText(
                                                        report,
                                                        locale
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {permissions.updateDevice ? (
                                            <>
                                                {report.report_type ===
                                                REPORT_TYPES.catch ? (
                                                    <Button
                                                        reverted
                                                        onClick={() =>
                                                            openReplaceTypeModal(
                                                                REPORT_TYPES.false_triggering
                                                            )
                                                        }
                                                        style={{
                                                            marginLeft: 10
                                                        }}
                                                    >
                                                        {
                                                            locale.reports
                                                                .reportTypes[
                                                                '17'
                                                            ]
                                                        }
                                                        ?
                                                    </Button>
                                                ) : null}
                                                {report.report_type ===
                                                REPORT_TYPES.false_triggering ? (
                                                    <Button
                                                        reverted
                                                        onClick={() =>
                                                            openReplaceTypeModal(
                                                                REPORT_TYPES.catch
                                                            )
                                                        }
                                                        style={{
                                                            marginLeft: 10
                                                        }}
                                                    >
                                                        {
                                                            locale.reports
                                                                .reportTypes[
                                                                '20'
                                                            ]
                                                        }
                                                        ?
                                                    </Button>
                                                ) : null}
                                            </>
                                        ) : null}
                                    </div>
                                    <div>
                                        {getReportTypeDescriptionText(
                                            report,
                                            locale
                                        )}
                                    </div>
                                </div>
                            </div>
                            {hasCatchDescription(report) && (
                                <div className={classes.detailsRow}>
                                    <div className={classes.detailsNameColumn}>
                                        {locale.reports.catchDescription}:
                                    </div>
                                    <div className={classes.detailsValueColumn}>
                                        {report.data.catch_description}
                                    </div>
                                </div>
                            )}
                            {report?.data?.tom_data?.comment &&
                                report.data.tom_data.comment !== '' && (
                                    <div className={classes.detailsRow}>
                                        <div
                                            className={
                                                classes.detailsNameColumn
                                            }
                                        >
                                            {locale.tom.comments}:
                                        </div>
                                        <div
                                            className={
                                                classes.detailsValueColumn
                                            }
                                        >
                                            {report.data.tom_data.comment}
                                        </div>
                                    </div>
                                )}
                            {report?.data?.tom_data?.content_type && (
                                <div className={classes.detailsRow}>
                                    <div className={classes.detailsNameColumn}>
                                        {locale.tom.contentType}:
                                    </div>
                                    <div className={classes.detailsValueColumn}>
                                        {
                                            locale.tom[
                                                report.data.tom_data
                                                    .content_type
                                            ]
                                        }
                                    </div>
                                </div>
                            )}
                            {report?.data?.tom_data?.infestation_amount !==
                                null &&
                                report?.data?.tom_data?.infestation_amount !==
                                    undefined && (
                                    <div className={classes.detailsRow}>
                                        <div
                                            className={
                                                classes.detailsNameColumn
                                            }
                                        >
                                            {locale.tom.amount}:
                                        </div>
                                        <div
                                            className={
                                                classes.detailsValueColumn
                                            }
                                        >
                                            {
                                                report.data.tom_data
                                                    .infestation_amount
                                            }
                                        </div>
                                    </div>
                                )}

                            {report.report_type === '2' && (
                                <div className={classes.detailsRow}>
                                    <div className={classes.detailsNameColumn}>
                                        {locale.reports.sharingHistory}:
                                    </div>
                                    <div className={classes.detailsValueColumn}>
                                        <div
                                            className={
                                                classes.detailsSharingHistoryBox
                                            }
                                        >
                                            {reportSharingHistory &&
                                            reportSharingHistory.length > 0 ? (
                                                reportSharingHistory.map(
                                                    (history, index) => (
                                                        <div
                                                            className={
                                                                classes.detailsSharingHistoryItem
                                                            }
                                                            key={index}
                                                        >
                                                            <div
                                                                className={
                                                                    classes.leftColumn
                                                                }
                                                            >
                                                                <div>{`${getFormattedDate(
                                                                    history.created_at
                                                                )}`}</div>
                                                            </div>
                                                            <div
                                                                onMouseEnter={e =>
                                                                    e.currentTarget.classList.add(
                                                                        classes.rightColumnHover
                                                                    )
                                                                }
                                                                onMouseLeave={e =>
                                                                    e.currentTarget.classList.remove(
                                                                        classes.rightColumnHover
                                                                    )
                                                                }
                                                                className={
                                                                    classes.rightColumn
                                                                }
                                                            >
                                                                {history.shared_by ? (
                                                                    <>
                                                                        <div
                                                                            className={
                                                                                classes.inlineDiv
                                                                            }
                                                                        >{`${locale.reports.sharedBy}: `}</div>
                                                                        <div
                                                                            className={
                                                                                classes.inlineDiv
                                                                            }
                                                                        >{`${history.shared_by}`}</div>
                                                                        <br />
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <div
                                                                            className={
                                                                                classes.inlineDiv
                                                                            }
                                                                        >
                                                                            {
                                                                                locale
                                                                                    .reports
                                                                                    .sentAutomatically
                                                                            }
                                                                        </div>
                                                                        <br />
                                                                    </>
                                                                )}
                                                                <div
                                                                    className={
                                                                        classes.inlineDiv
                                                                    }
                                                                >{`${locale.reports.toEmails}: `}</div>
                                                                <div
                                                                    className={
                                                                        classes.inlineDiv
                                                                    }
                                                                >{`${history.to_emails}`}</div>
                                                                <br />
                                                                {history.cc_emails && (
                                                                    <>
                                                                        <div
                                                                            className={
                                                                                classes.inlineDiv
                                                                            }
                                                                        >{`${locale.reports.ccEmails}: `}</div>
                                                                        <div
                                                                            className={
                                                                                classes.inlineDiv
                                                                            }
                                                                        >{`${history.cc_emails}`}</div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )
                                                )
                                            ) : (
                                                <div>
                                                    {
                                                        locale.reports
                                                            .noSharingHistory
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className={classes.commentTextSection}>
                                <hr style={{ marginBottom: '20px' }} />
                                {locale.reports.comments}:
                            </div>
                            {!addComment ? (
                                <div className={classes.textareaSection}>
                                    <div
                                        className={classes.detailsNameColumn}
                                    ></div>
                                    <div className={classes.commentInfo}>
                                        {permissions.canAddComment && (
                                            <p
                                                className={
                                                    classes.addCommentButton
                                                }
                                                onClick={() => {
                                                    setAddComment(true),
                                                        setCommentText(''),
                                                        setIsEdit(false)
                                                }}
                                            >
                                                {locale.reports.addComment}
                                            </p>
                                        )}
                                        {imagesLoader === 'post' && <Loader />}
                                    </div>
                                </div>
                            ) : null}
                            <CommentCard.NewComment
                                addComment={addComment}
                                commentId={commentId}
                                setCommentText={setCommentText}
                                commentText={commentText}
                                locale={locale}
                                imagesForUpload={imagesForUpload}
                                setImagesForUpload={setImagesForUpload}
                                deletedPictureKeys={deletedPictureKeys}
                                setDeletedPictureKeys={setDeletedPictureKeys}
                                isEdit={true}
                                postComment={postComment}
                                report={report}
                                setAddComment={setAddComment}
                                setIsEdit={setIsEdit}
                            />

                            {comments != null && comments.length !== 0
                                ? comments.map((comment, index) => {
                                      const canManageComment =
                                          (UserPermissions.isAdmin() &&
                                              report.is_original) ||
                                          loggedInUser.id === comment.user_id
                                      const isCommentInEditMode =
                                          commentId === comment.id

                                      if (comment != null) {
                                          let datetime = comment.updated_at
                                              ? getFormattedDate(
                                                    comment.updated_at
                                                )
                                              : getFormattedDate(
                                                    comment.created_at
                                                )
                                          let date = datetime.slice(0, 10)
                                          let time = datetime.slice(10, 19)

                                          return (
                                              <>
                                                  <CommentCard
                                                      key={index}
                                                      comment={comment}
                                                      getMap={getMap}
                                                  >
                                                      <CommentCard.Header
                                                          comment={comment}
                                                          date={date}
                                                          locale={locale}
                                                          time={time}
                                                          commentId={comment.id}
                                                          isCommentInEditMode={
                                                              isCommentInEditMode
                                                          }
                                                      />

                                                      <CommentCard.Body
                                                          comment={comment}
                                                          isCommentInEditMode={
                                                              isCommentInEditMode
                                                          }
                                                      />

                                                      {canManageComment ? (
                                                          <CommentCard.Footer
                                                              comment={comment}
                                                              isCommentInEditMode={
                                                                  isCommentInEditMode
                                                              }
                                                              editComment={
                                                                  editComment
                                                              }
                                                              locale={locale}
                                                              setIsDeleteCommentModalOpen={
                                                                  setIsDeleteCommentModalOpen
                                                              }
                                                              isDeleteCommentModalOpen={
                                                                  isDeleteCommentModalOpen
                                                              }
                                                              setCommentIdToDelete={
                                                                  setCommentIdToDelete
                                                              }
                                                              deleteComment={
                                                                  deleteComment
                                                              }
                                                              commentIdToDelete={
                                                                  commentIdToDelete
                                                              }
                                                              report={report}
                                                          />
                                                      ) : null}
                                                      <CommentCard.Edit
                                                          addComment={
                                                              addComment
                                                          }
                                                          isCommentInEditMode={
                                                              isCommentInEditMode
                                                          }
                                                          setCommentText={
                                                              setCommentText
                                                          }
                                                          commentText={
                                                              commentText
                                                          }
                                                          locale={locale}
                                                          imagesForUpload={
                                                              imagesForUpload
                                                          }
                                                          setImagesForUpload={
                                                              setImagesForUpload
                                                          }
                                                          deletedPictureKeys={
                                                              deletedPictureKeys
                                                          }
                                                          setDeletedPictureKeys={
                                                              setDeletedPictureKeys
                                                          }
                                                          isEdit={isEdit}
                                                          postComment={
                                                              postComment
                                                          }
                                                          report={report}
                                                          setAddComment={
                                                              setAddComment
                                                          }
                                                          setIsEdit={setIsEdit}
                                                          setCommentId={
                                                              setCommentId
                                                          }
                                                      />
                                                  </CommentCard>
                                              </>
                                          )
                                      }
                                  })
                                : null}
                        </div>

                        <div className={classes.detailsInfoWrapperRight}>
                            {report.tom_cam_picture_id &&
                                report.tom_cam_picture && (
                                    <>
                                        <img
                                            onClick={() => lightbox.on()}
                                            className={classes.tomPicture}
                                            src={tomPictureFileLink}
                                        />
                                        {lightbox.is && (
                                            <Lightbox
                                                animationDuration={200}
                                                reactModalStyle={{
                                                    overlay: { zIndex: 3000 }
                                                }}
                                                mainSrc={tomPictureFileLink}
                                                onCloseRequest={lightbox.off}
                                            />
                                        )}
                                    </>
                                )}
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default withRouter(Report)
